const API_URL = 'https://gamescon-backend-559ca0f1a07e.herokuapp.com/api';

// ==========================================
// INTERFACES (Tipagens baseadas no seu schema)
// ==========================================

export interface User {
  id: number;
  documentId: string;
  username: string;
  email: string;
  name?: string;
  gender?: string;
  birthday?: string;
  cpf?: string;
  terms?: boolean;
  confirmed?: boolean;
  blocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface StrapiError {
  status: number;
  name: string;
  message: string;
  details?: any;
}

export interface AuthResponse {
  jwt?: string; // Opcional, pois não vem se der erro
  user?: User;  // Opcional
  error?: StrapiError; // Opcional, pois não vem se der sucesso
}

export interface LoginPayload {
  identifier: string; // O Strapi aceita tanto o email quanto o username aqui
  password: string;
}

// Omitimos dados internos e permitimos os campos customizados que você criou
export interface RegisterPayload {
  username: string;
  email: string;
  password: string;
  name?: string;
  gender?: string;
  birthday?: string;
  cpf?: string;
  terms?: boolean;
}

export interface CodeResponse {
  message: string;
  code?: string; // Opcional, pois se der erro, ele não vem
}
export interface ActiveResponse {
  data: { active: boolean; }  
}

export type SurveyRecord = Record<string, string | number | boolean | null>;

export type SurveyStorage = {
  surveys: Record<string, { data: SurveyRecord }>;
};

export interface RatingsResponse {
  message?: string;
  data?: any;
  error?: StrapiError;
}
// ==========================================
// FUNÇÕES DE REQUISIÇÃO
// ==========================================

export const api = {
  /**
   * Realiza o login do usuário
   */
  async login(data: LoginPayload): Promise<AuthResponse> {
    const response = await fetch(`${API_URL}/auth/local`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      // O Strapi geralmente manda o erro detalhado em result.error.message
      throw new Error(result.error?.message || 'Erro ao fazer login.');
    }

    return result;
  },

  /**
   * Registra um novo usuário
   */
  async register(data: RegisterPayload): Promise<AuthResponse> {
    const response = await fetch(`${API_URL}/auth/local/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    /* if (!response.ok) {
      throw new Error(result.error?.message || 'Erro ao registrar usuário.');
    } */

    return result;
  },

  /**
   * Gera um código de brinde para o usuário logado
   * @param jwt Token JWT recebido no login/registro
   */
  async generateCode(): Promise<CodeResponse> {
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/codes/generate`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Aqui enviamos o token para o backend saber quem está pedindo o código
        'Authorization': `Bearer ${jwt}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      // Se ele já tiver um código, nós tratamos lá no backend pra retornar um erro ou a própria mensagem.
      // Dependendo de como você montou, pode ser que response.ok seja falso se der 400.
      throw new Error(result.error?.message || result.message || 'Erro ao gerar o código.');
    }

    return result;
  },

  /**
   * Pega todos os dados do usuário logado (incluindo o currentStage)
   * Ideal para chamar quando o jogo carrega!
   */
  async getMe() {
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/users/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`,
      },
    });

    const result = await response.json();
    if (!response.ok) throw new Error('Erro ao carregar os dados do jogador.');

    return result; // Aqui virá { id: 1, username: "...", currentStage: 1, ... }
  },

  /**
   * Salva a fase atual do jogador
   */
  async saveStage(stageNumber: number) {
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/save-progress`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
      },
      body: JSON.stringify({ stage: stageNumber }),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Erro ao salvar o jogo.');

    return result;
  },

  async getMyCodes() {
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/codes/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`,
      },
    });

    const result = await response.json();
    if (!response.ok) throw new Error('Erro ao carregar o histórico de códigos.');

    return result;
  },

  async sendNeozone(data: any) {
    const jwt = localStorage.getItem('jwt');

    const response = await fetch(`${API_URL}/neozones`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
      },
      body: JSON.stringify({ data }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error?.message || 'Erro ao enviar neozone');
    }

    return result;
  },

  async sendOpenzone(data: any) {
    const jwt = localStorage.getItem('jwt');

    const response = await fetch(`${API_URL}/ratings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
      },
      body: JSON.stringify({ data }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error?.message || 'Erro ao enviar openzone');
    }

    return result;
  },

  async sendFinal(data: any) {
    const jwt = localStorage.getItem('jwt');

    const response = await fetch(`${API_URL}/rating-finals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
      },
      body: JSON.stringify({ data }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error?.message || 'Erro ao enviar final');
    }

    return result;
  },
    async isActive(): Promise<ActiveResponse> {
    const response = await fetch(`${API_URL}/actives/smhqnmg6356byro9113fq650`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error?.message || result.message || 'Erro ao gerar o código.');
    }

    return result;
  },

};